import { useEffect, useRef, useState } from 'react';
import { endpoints } from '../../../api';
import type { HistoryOrder } from '../../../api/history';
import { deliveryFormats } from '../../history/history-model';
import { deliveryIntent, clearDeliveryIntent } from '../../history/history-delivery-intent';

/** Используется существующая квитанция доставки: неопределённый POST повторяется с прежним ключом. */
export function useCircleDelivery(orderId: number, ready: boolean) {
  const [detail, setDetail] = useState<HistoryOrder | null>(null),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(false);
  const owner = useRef<number | null>(null),
    inFlight = useRef(false),
    controller = useRef<AbortController | null>(null);
  useEffect(() => {
    if (!ready) return;
    const request = new AbortController();
    void Promise.all([endpoints.me(), endpoints.history.get(orderId, request.signal)])
      .then(([me, order]) => {
        if (!request.signal.aborted) {
          owner.current = me.user.id;
          setDetail(order);
          setError('');
        }
      })
      .catch(() => {
        if (!request.signal.aborted) setError('Не удалось проверить доставку. Откройте историю заказа.');
      });
    return () => request.abort();
  }, [orderId, ready]);
  useEffect(() => () => controller.current?.abort(), []);
  const state = detail?.delivery?.state;
  useEffect(() => {
    if (!['pending', 'accepted', 'queued', 'sending', 'uncertain'].includes(state ?? '')) return;
    const request = new AbortController();
    const timer = setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      void endpoints.history
        .get(orderId, request.signal)
        .then(order => {
          if (!request.signal.aborted) setDetail(order);
        })
        .catch(() => {});
    }, 4000);
    return () => {
      request.abort();
      clearInterval(timer);
    };
  }, [orderId, state]);
  async function send(format: string) {
    if (!detail || inFlight.current || !deliveryFormats(detail).includes(format)) return;
    inFlight.current = true;
    setBusy(true);
    setError('');
    const request = new AbortController();
    controller.current = request;
    try {
      const me = await endpoints.me();
      if (request.signal.aborted) return;
      if (me.user.id !== owner.current) throw new Error('owner_changed');
      const current = await endpoints.history.get(orderId, request.signal);
      if (request.signal.aborted) return;
      if (current.status !== 'done' || !current.result?.available || current.result_revision !== detail.result_revision)
        throw new Error('result_changed');
      if (['sent', 'delivered'].includes(current.delivery?.state ?? '')) {
        setDetail(current);
        return;
      }
      const intent = deliveryIntent(me.user.id, orderId, current.result_revision ?? 1, format);
      const result = await endpoints.history.send(current, format, intent.key, request.signal);
      if (!request.signal.aborted) setDetail({ ...current, ...result });
      if (['sent', 'delivered'].includes(result.delivery?.state ?? '')) clearDeliveryIntent(intent);
    } catch {
      if (!request.signal.aborted) setError('Результат отправки пока не подтверждён. Продолжите проверку с тем же ключом.');
    } finally {
      inFlight.current = false;
      if (!request.signal.aborted) setBusy(false);
    }
  }
  return { detail, busy, error, send, formats: detail ? deliveryFormats(detail) : [], state };
}
